import { Subject } from 'rxjs';

export class BoolTreeNode<TAttrs> {
  public readonly attrs?: TAttrs;
  private readonly _onCheck$ = new Subject<void>();
  private _value: boolean | Readonly<BoolTreeNode<TAttrs>[]>;
  public readonly onCheck$ = this._onCheck$.asObservable();

  // computed[ACCESSOR] is used to avoid endless loops in Angular change detection;
  private computed: {
    selectedChildren?: Readonly<BoolTreeNode<TAttrs>[]>;
    indeterminate?: boolean;
    value?: boolean;
  } = { };

  constructor(args: {
    value?: boolean | Array<BoolTreeNode<TAttrs>>;
    attrs?: TAttrs;
  }) {
    this.attrs = args.attrs;

    if (!(args.value instanceof Array)) {
      this._value = args.value ?? false;
    } else {
      this._value = Object.freeze(args.value);
      for (const child of this._value) {
        child.onCheck$.subscribe(() => {
          this.doCheck();
        });
      }
    }

    this.doCheck();
  }

  get value(): boolean {
    return this.computed.value;
  }

  set value(value: boolean) {
    if (this._value instanceof Array) {
      for (const child of this._value) {
        child._value = value;
        child.doCheck({
          avoidOnCheck: true,
        });
      }
    } else {
      this._value = value;
    }

    this.doCheck();
  }

  get indeterminate(): boolean {
    return Boolean(this.computed.indeterminate);
  }

  get children(): Readonly<BoolTreeNode<TAttrs>[]> | undefined {
    return this._value instanceof Array ? this._value : undefined;
  }

  get selectedChildren(): Readonly<BoolTreeNode<TAttrs>[]> | undefined {
    return this.computed.selectedChildren;
  }

  private doCheck(args: { avoidOnCheck?: boolean } = {}): void {
    if (this._value instanceof Array) {
      let hasFalse = false,
        hasTrue = false;
      const selectedChildren = [];

      for (const treeNode of this._value) {
        if (treeNode.value) {
          selectedChildren.push(treeNode);
          hasTrue = true;
        } else {
          hasFalse = true;
        }
      }

      this.computed.selectedChildren = Object.freeze(selectedChildren);
      this.computed.indeterminate = hasFalse && hasTrue;
      this.computed.value = !hasFalse;
    } else {
      this.computed.value = Boolean(this._value);
      this.computed.selectedChildren = undefined;
      this.computed.indeterminate = false;
    }

    if (args.avoidOnCheck) return;
    this._onCheck$.next();
  }

}
